import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { api } from '@client/lib/api';
import { currentLocale } from '@client/lib/i18n';
import { useTurnstile } from '@client/lib/use-turnstile';
import { Button } from '@client/components/ui/button';
import { Field, Input } from '@client/components/ui/input';
import { MarkWhite, Wordmark } from '@client/components/brand/logo';
import { LangSwitch } from '@client/components/lang-switch';

const searchSchema = z.object({ error: z.string().optional(), redirect: z.string().optional(), email: z.string().optional() });

export const Route = createFileRoute('/login')({
  validateSearch: searchSchema,
  component: LoginPage,
});

function LoginPage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const [email, setEmail] = React.useState(search.email ?? '');
  const [sentTo, setSentTo] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(search.error === 'expired' ? t('login.expired') : search.error === 'reauth' ? t('login.reauth') : null);
  const [busy, setBusy] = React.useState(false);
  const { ref: turnstileRef, token: turnstileToken, siteKey } = useTurnstile();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setError(t('login.invalid'));
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await api('/api/auth/magic-link', { body: { email: value, locale: currentLocale(), turnstile_token: turnstileToken, redirect_to: search.redirect } });
      setSentTo(value);
    } catch {
      setError(t('errors.network'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-dvh grid-cols-1 md:grid-cols-[44%_56%]">
      <section className="hero-navy relative flex flex-col justify-between p-8 text-white md:p-12">
        <div className="flex items-center gap-3">
          <MarkWhite size={36} />
          <span className="text-[15px] font-[700] tracking-[0.02em]">Investor Room</span>
        </div>
        <div className="my-16 max-w-md">
          <h1 className="text-[32px] font-[700] leading-[38px] tracking-[-0.03em] text-white md:text-[38px] md:leading-[44px]">{t('app.tagline')}</h1>
        </div>
        <p className="max-w-md text-[13px] leading-5 text-navy-muted">{t('app.transparency')}</p>
      </section>
      <section className="relative flex items-center justify-center bg-card px-6 py-12 md:px-12">
        <div className="absolute right-6 top-6">
          <LangSwitch />
        </div>
        <div className="w-full max-w-[400px]">
          <Wordmark width={150} className="mb-10" />
          {sentTo ? (
            <div>
              <h2 className="page-title">{t('login.checkInbox')}</h2>
              <p className="mt-3 text-[16px] leading-6 text-body">{t('login.sentTo', { email: sentTo })}</p>
              <Button variant="ghost" className="mt-6 -ml-3" onClick={() => setSentTo(null)}>
                {t('login.useAnother')}
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} noValidate>
              <h2 className="page-title">{t('login.title')}</h2>
              <p className="mb-6 mt-3 text-[16px] leading-6 text-body">{t('login.subtitle')}</p>
              <Field label={t('login.email')} htmlFor="email" error={error ?? undefined}>
                <Input id="email" type="email" inputMode="email" autoComplete="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@firm.com" className="h-11 text-[15px]" />
              </Field>
              {siteKey ? <div ref={turnstileRef} className="mb-4 min-h-[65px]" /> : null}
              <Button type="submit" size="lg" className="w-full" loading={busy}>
                {busy ? t('login.sending') : t('login.submit')}
              </Button>
              <p className="mt-4 text-[13px] text-muted">{t('login.noPassword')}</p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
